import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  User, Shield, Save, Building2, Calendar,
  Mail, Phone, Clock, Activity, RefreshCw,
  UserCheck, CheckCircle, XCircle, KeyRound
} from 'lucide-react';

// Helper to get auth config for axios requests (mobile compatibility)
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

export default function SuperAdminUserDetail() {
  const { user: currentUser, API } = useContext(AppContext);
  const { userId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [membership, setMembership] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [allTenants, setAllTenants] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    sms_enabled: false,
    status: 'active',
    tenant_id: '',
    role: 'player',
    season_ids: []
  });

  // Auth conversion state
  const [showConvertAuth, setShowConvertAuth] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [convertingAuth, setConvertingAuth] = useState(false);
  const [authDebugInfo, setAuthDebugInfo] = useState(null);
  const [testPassword, setTestPassword] = useState('');
  const [testLoginResult, setTestLoginResult] = useState(null);
  const [testingLogin, setTestingLogin] = useState(false);
  const [fullLoginDebug, setFullLoginDebug] = useState(null);
  const [testingFullLogin, setTestingFullLogin] = useState(false);

  useEffect(() => {
    if (!currentUser?.is_super_admin) {
      navigate('/home');
      return;
    }
    loadUserData();
    loadTenants();
  }, [currentUser, userId]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/platform/users/${userId}`, getAuthConfig());
      const data = response.data;
      
      setUserData(data.user);
      setMembership(data.membership);
      setTenant(data.tenant);
      setAvailableSeasons(data.available_seasons || []);
      setRecentActivity(data.recent_activity || []);
      
      setFormData({
        name: data.user.name || '',
        email: data.user.email || '',
        phone: data.user.phone_number || data.user.phone || '',
        sms_enabled: data.user.sms_enabled || false,
        status: data.user.status || 'active',
        tenant_id: data.membership?.tenant_id || '',
        role: data.membership?.role || 'player',
        season_ids: data.membership?.season_ids || []
      });
    } catch (error) {
      console.error('Failed to load user:', error);
      toast.error('Failed to load user data');
      navigate('/super-admin/users');
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const response = await axios.get(`${API}/platform/tenants-list`, getAuthConfig());
      setAllTenants(response.data);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const loadSeasonsForTenant = async (tenantId) => {
    if (!tenantId) {
      setAvailableSeasons([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/platform/tenants/${tenantId}/seasons`, getAuthConfig());
      setAvailableSeasons(response.data);
    } catch (error) {
      console.error('Failed to load seasons for tenant:', error);
    }
  };

  const handleTenantChange = async (newTenantId) => {
    setFormData(prev => ({
      ...prev,
      tenant_id: newTenantId,
      season_ids: []
    }));
    await loadSeasonsForTenant(newTenantId);
  };

  const handleSeasonToggle = (seasonId) => {
    setFormData(prev => {
      const newSeasonIds = prev.season_ids.includes(seasonId)
        ? prev.season_ids.filter(id => id !== seasonId)
        : [...prev.season_ids, seasonId];
      return { ...prev, season_ids: newSeasonIds };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API}/platform/users/${userId}`, formData, getAuthConfig());
      toast.success('User updated successfully');
      loadUserData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async () => {
    if (!window.confirm('Suspend this user?')) return;
    try {
      await axios.post(`${API}/platform/users/${userId}/suspend`, null, getAuthConfig());
      toast.success('User suspended');
      loadUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleReactivate = async () => {
    try {
      await axios.post(`${API}/platform/users/${userId}/reactivate`, {}, getAuthConfig());
      toast.success('User reactivated');
      loadUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    }
  };

  const handleConvertToEmailAuth = async () => {
    // Validate passwords
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setConvertingAuth(true);
      await axios.post(
        `${API}/platform/users/${userId}/convert-to-email-auth`,
        { new_password: newPassword },
        getAuthConfig()
      );
      toast.success('User converted to email/password authentication');
      setShowConvertAuth(false);
      setNewPassword('');
      setConfirmPassword('');
      loadUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert authentication');
    } finally {
      setConvertingAuth(false);
    }
  };

  const handleAuthDebug = async () => {
    try {
      const response = await axios.get(`${API}/platform/users/${userId}/auth-debug`, getAuthConfig());
      setAuthDebugInfo(response.data);
      toast.success('Auth debug info loaded - see details below');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get auth debug info');
    }
  };

  const handleTestLogin = async () => {
    if (!testPassword) {
      toast.error('Please enter a password to test');
      return;
    }
    try {
      setTestingLogin(true);
      setTestLoginResult(null);
      const response = await axios.post(
        `${API}/platform/users/${userId}/test-login`,
        { password: testPassword },
        getAuthConfig()
      );
      setTestLoginResult(response.data);
      if (response.data.success) {
        toast.success('Password verification PASSED!');
      } else {
        toast.error(`Password verification FAILED: ${response.data.reason}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to test login');
    } finally {
      setTestingLogin(false);
    }
  };

  const handleFullLoginDebug = async () => {
    if (!userData?.email || !testPassword) {
      toast.error('Please enter a password first');
      return;
    }
    try {
      setTestingFullLogin(true);
      setFullLoginDebug(null);
      const response = await axios.post(
        `${API}/platform/debug-login`,
        { email: userData.email, password: testPassword },
        getAuthConfig()
      );
      setFullLoginDebug(response.data);
      if (response.data.success) {
        toast.success('Full login flow would SUCCEED!');
      } else {
        toast.error(`Full login flow FAILED: ${response.data.final_result}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to debug login');
    } finally {
      setTestingFullLogin(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-600 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case 'suspended':
        return <Badge className="bg-red-600 text-xs"><XCircle className="w-3 h-3 mr-1" />Suspended</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-600 text-xs"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <SuperAdminLayout title="User Details" backLink="/super-admin/users">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  if (!userData) {
    return (
      <SuperAdminLayout title="User Details" backLink="/super-admin/users">
        <div className="text-center py-12 text-zinc-400">User not found</div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout title={userData.name} subtitle={userData.email} backLink="/super-admin/users" onRefresh={loadUserData}>
      <div className="max-w-4xl mx-auto space-y-4">
        {/* User Header Card */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                {userData.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-lg sm:text-xl font-bold text-white truncate">{userData.name}</h1>
                  {getStatusBadge(userData.status)}
                  {userData.is_super_admin && (
                    <Badge className="bg-amber-600 text-xs"><Shield className="w-3 h-3 mr-1" />Super Admin</Badge>
                  )}
                </div>
                <p className="text-zinc-400 text-sm truncate">{userData.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList className="bg-zinc-900 border border-zinc-800 w-full justify-start overflow-x-auto">
            <TabsTrigger value="profile" className="data-[state=active]:bg-emerald-600 text-xs sm:text-sm">
              <User className="w-4 h-4 mr-1 sm:mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="tenant" className="data-[state=active]:bg-emerald-600 text-xs sm:text-sm">
              <Building2 className="w-4 h-4 mr-1 sm:mr-2" /> Tenant
            </TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-emerald-600 text-xs sm:text-sm">
              <Activity className="w-4 h-4 mr-1 sm:mr-2" /> Activity
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-white text-base sm:text-lg">Profile Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-1">
                    <Label className="text-zinc-300 text-sm">Full Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-300 text-sm">Email</Label>
                    <Input
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm"
                      type="email"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-300 text-sm">Phone</Label>
                    <Input
                      value={formData.phone || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="bg-zinc-800 border-zinc-700 text-white text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-300 text-sm">SMS Notifications</Label>
                    <div className="flex items-center gap-3 h-10">
                      <Checkbox
                        id="sms-enabled"
                        checked={formData.sms_enabled}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, sms_enabled: checked }))}
                        disabled={!formData.phone}
                      />
                      <label htmlFor="sms-enabled" className={`text-sm ${formData.sms_enabled ? 'text-emerald-400' : 'text-zinc-400'}`}>
                        {formData.sms_enabled ? 'Opted In' : 'Opted Out'}
                      </label>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-zinc-300 text-sm">Status</Label>
                    <Select 
                      value={formData.status} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Account Details */}
                <div className="pt-3 border-t border-zinc-800">
                  <h3 className="text-white font-medium text-sm mb-2">Account Details</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div>
                      <span className="text-zinc-400">Created:</span>
                      <span className="text-white ml-1">
                        {new Date(userData.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-zinc-400">Provider:</span>
                      <span className="text-white ml-1">{userData.auth_provider || 'Email'}</span>
                    </div>
                  </div>
                  
                  {/* Password Management - Show for all users */}
                  <div className="mt-4 p-3 bg-zinc-800 rounded-lg">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-amber-500" />
                        <div>
                          <p className="text-white text-sm font-medium">
                            {userData.auth_provider === 'google' ? 'Google Sign-In User' : 'Email/Password User'}
                          </p>
                          <p className="text-zinc-400 text-xs">
                            {userData.auth_provider === 'google' 
                              ? 'Convert to email/password login' 
                              : 'Reset user password'}
                          </p>
                        </div>
                      </div>
                      <Dialog open={showConvertAuth} onOpenChange={setShowConvertAuth}>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-amber-600 text-amber-500 hover:bg-amber-600/10"
                            data-testid="convert-auth-btn"
                          >
                            <KeyRound className="w-4 h-4 mr-2" />
                            {userData.auth_provider === 'google' ? 'Set Password' : 'Reset Password'}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-zinc-900 border-zinc-700">
                          <DialogHeader>
                            <DialogTitle className="text-white">
                              {userData.auth_provider === 'google' ? 'Convert to Email/Password' : 'Reset Password'}
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <p className="text-sm text-zinc-400">
                              {userData.auth_provider === 'google' 
                                ? <>Set a password for <strong className="text-white">{userData.name}</strong> to allow them to log in with email and password instead of Google.</>
                                : <>Set a new password for <strong className="text-white">{userData.name}</strong>.</>
                              }
                            </p>
                            <div className="space-y-2">
                              <Label className="text-zinc-300">New Password</Label>
                              <Input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                                className="bg-zinc-800 border-zinc-700 text-white"
                                data-testid="convert-new-password"
                              />
                              <p className="text-xs text-zinc-500">Minimum 8 characters</p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-zinc-300">Confirm Password</Label>
                              <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm password"
                                className="bg-zinc-800 border-zinc-700 text-white"
                                data-testid="convert-confirm-password"
                              />
                            </div>
                            <div className="flex gap-2 justify-end pt-2">
                              <Button 
                                variant="outline" 
                                onClick={() => {
                                  setShowConvertAuth(false);
                                  setNewPassword('');
                                  setConfirmPassword('');
                                }}
                                className="border-zinc-700 text-zinc-300"
                              >
                                Cancel
                              </Button>
                              <Button 
                                onClick={handleConvertToEmailAuth}
                                disabled={convertingAuth || !newPassword || !confirmPassword}
                                className="bg-amber-600 hover:bg-amber-700"
                                data-testid="confirm-convert-auth-btn"
                              >
                                {convertingAuth ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    {userData.auth_provider === 'google' ? 'Converting...' : 'Resetting...'}
                                  </>
                                ) : (
                                  userData.auth_provider === 'google' ? 'Convert to Email Auth' : 'Reset Password'
                                )}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  
                  {/* Auth Debug Section */}
                  <div className="mt-4 p-3 bg-zinc-900 rounded-lg border border-zinc-700">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-500" />
                        <div>
                          <p className="text-white text-sm font-medium">Auth Debug</p>
                          <p className="text-zinc-400 text-xs">Check password hash status</p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleAuthDebug}
                        className="border-blue-600 text-blue-500 hover:bg-blue-600/10"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Check Auth
                      </Button>
                    </div>
                    
                    {authDebugInfo && (
                      <div className="mt-3 p-3 bg-zinc-800 rounded text-xs font-mono space-y-1">
                        <div><span className="text-zinc-400">Email:</span> <span className="text-white">{authDebugInfo.email}</span></div>
                        <div><span className="text-zinc-400">Auth Provider:</span> <span className="text-white">{authDebugInfo.auth_provider}</span></div>
                        <div><span className="text-zinc-400">Has Password Hash:</span> <span className={authDebugInfo.has_password_hash ? "text-green-500" : "text-red-500"}>{authDebugInfo.has_password_hash ? 'YES' : 'NO'}</span></div>
                        <div><span className="text-zinc-400">Hash Length:</span> <span className="text-white">{authDebugInfo.password_hash_length}</span></div>
                        <div><span className="text-zinc-400">Hash Prefix:</span> <span className="text-white">{authDebugInfo.password_hash_prefix || 'N/A'}</span></div>
                        <div><span className="text-zinc-400">Valid bcrypt Format:</span> <span className={authDebugInfo.password_hash_valid_format ? "text-green-500" : "text-red-500"}>{authDebugInfo.password_hash_valid_format ? 'YES' : 'NO'}</span></div>
                      </div>
                    )}
                    
                    {/* Test Login */}
                    <div className="mt-4 pt-4 border-t border-zinc-700">
                      <p className="text-white text-sm font-medium mb-2">Test Password</p>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          value={testPassword}
                          onChange={(e) => setTestPassword(e.target.value)}
                          placeholder="Enter password to test"
                          className="bg-zinc-800 border-zinc-700 text-white flex-1"
                        />
                        <Button 
                          size="sm"
                          onClick={handleTestLogin}
                          disabled={testingLogin || !testPassword}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {testingLogin ? 'Testing...' : 'Test'}
                        </Button>
                      </div>
                      
                      {testLoginResult && (
                        <div className={`mt-3 p-3 rounded text-xs font-mono ${testLoginResult.success ? 'bg-green-900/50 border border-green-600' : 'bg-red-900/50 border border-red-600'}`}>
                          <div className="font-bold mb-2">
                            {testLoginResult.success ? '✅ PASSWORD MATCHES' : '❌ PASSWORD DOES NOT MATCH'}
                          </div>
                          <div><span className="text-zinc-400">Reason:</span> <span className="text-white">{testLoginResult.reason}</span></div>
                          {testLoginResult.details && (
                            <>
                              <div><span className="text-zinc-400">Password Length:</span> <span className="text-white">{testLoginResult.details.password_length}</span></div>
                              <div><span className="text-zinc-400">Has Leading Space:</span> <span className={testLoginResult.details.password_has_leading_space ? "text-yellow-500" : "text-white"}>{testLoginResult.details.password_has_leading_space ? 'YES ⚠️' : 'No'}</span></div>
                              <div><span className="text-zinc-400">Has Trailing Space:</span> <span className={testLoginResult.details.password_has_trailing_space ? "text-yellow-500" : "text-white"}>{testLoginResult.details.password_has_trailing_space ? 'YES ⚠️' : 'No'}</span></div>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Full Login Debug Button */}
                      <Button 
                        size="sm"
                        onClick={handleFullLoginDebug}
                        disabled={testingFullLogin || !testPassword}
                        className="mt-3 w-full bg-purple-600 hover:bg-purple-700"
                      >
                        {testingFullLogin ? 'Testing Full Flow...' : 'Test Full Login Flow'}
                      </Button>
                      
                      {fullLoginDebug && (
                        <div className={`mt-3 p-3 rounded text-xs font-mono ${fullLoginDebug.success ? 'bg-green-900/50 border border-green-600' : 'bg-red-900/50 border border-red-600'}`}>
                          <div className="font-bold mb-2">
                            {fullLoginDebug.success ? '✅ FULL LOGIN WOULD SUCCEED' : '❌ FULL LOGIN WOULD FAIL'}
                          </div>
                          <div className="mb-2"><span className="text-zinc-400">Final Result:</span> <span className="text-white">{fullLoginDebug.final_result}</span></div>
                          <div className="border-t border-zinc-600 pt-2 mt-2">
                            <div className="text-zinc-400 mb-1">Step-by-step:</div>
                            {fullLoginDebug.steps?.map((step, i) => (
                              <div key={i} className={`ml-2 ${step.status === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
                                {step.step}. {step.action}: {step.status}
                                {step.stored_email && <span className="text-zinc-400"> (stored: {step.stored_email})</span>}
                                {step.similar_emails?.length > 0 && (
                                  <div className="ml-4 text-yellow-400">Similar emails found: {step.similar_emails.join(', ')}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row justify-between gap-3 pt-3 border-t border-zinc-800">
                  <div>
                    {userData.status === 'active' ? (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={handleSuspend}
                        disabled={userData.is_super_admin}
                        className="w-full sm:w-auto"
                      >
                        <XCircle className="w-4 h-4 mr-2" /> Suspend
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleReactivate}
                        className="border-green-600 text-green-500 w-full sm:w-auto"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> Reactivate
                      </Button>
                    )}
                  </div>
                  <Button 
                    onClick={handleSave} 
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tenant Tab */}
          <TabsContent value="tenant">
            <div className="space-y-4">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="text-white text-base sm:text-lg">Tenant Assignment</CardTitle>
                  <CardDescription className="text-zinc-400 text-xs sm:text-sm">Assign user to a league</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-zinc-300 text-sm">Tenant / League</Label>
                      <Select 
                        value={formData.tenant_id || 'none'} 
                        onValueChange={(value) => handleTenantChange(value === 'none' ? '' : value)}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white text-sm">
                          <SelectValue placeholder="Select a tenant" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Tenant</SelectItem>
                          {allTenants.map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.league_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-300 text-sm">Role</Label>
                      <Select 
                        value={formData.role} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
                        disabled={!formData.tenant_id}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="player">Player</SelectItem>
                          <SelectItem value="moderator">Moderator</SelectItem>
                          <SelectItem value="league_admin">League Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-zinc-900 border-zinc-800">
                <CardHeader className="pb-2 sm:pb-4">
                  <CardTitle className="text-white text-base sm:text-lg">Season Access</CardTitle>
                </CardHeader>
                <CardContent>
                  {!formData.tenant_id ? (
                    <p className="text-zinc-400 text-sm">Assign a tenant to manage seasons</p>
                  ) : availableSeasons.length === 0 ? (
                    <p className="text-zinc-400 text-sm">No seasons available</p>
                  ) : (
                    <div className="space-y-2">
                      {availableSeasons.map(season => (
                        <div key={season.id} className="flex items-center gap-3 p-2 sm:p-3 bg-zinc-800 rounded-lg">
                          <Checkbox
                            id={season.id}
                            checked={formData.season_ids.includes(season.id)}
                            onCheckedChange={() => handleSeasonToggle(season.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <label htmlFor={season.id} className="text-white text-sm cursor-pointer">
                              Season {season.number}
                            </label>
                            <p className="text-zinc-400 text-xs">{season.status}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-4 flex justify-end">
                    <Button 
                      onClick={handleSave} 
                      disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
                    >
                      {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-white text-base sm:text-lg">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {recentActivity.length === 0 ? (
                  <p className="text-zinc-400 text-sm text-center py-8">No recent activity</p>
                ) : (
                  <div className="space-y-2">
                    {recentActivity.map((event, index) => (
                      <div key={index} className="flex gap-3 p-2 sm:p-3 bg-zinc-800 rounded-lg">
                        <Activity className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm">{event.action}</p>
                          <p className="text-zinc-400 text-xs">
                            {new Date(event.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </SuperAdminLayout>
  );
}
